"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Building2, House as Home, SunMedium, Landmark, Building, ArrowRight, PhoneCall, ShieldCheck, CircleCheck as CheckCircle2, HardHat } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
const buyerSectors = [{
  title: "Main contractors & developers",
  description: "Reliable phased access, engineered drawings, and strict health & safety compliance for new builds and major projects.",
  to: "/MainContractorsDevelopersPage",
  icon: Building2
}, {
  title: "Roofing contractors",
  description: "Rapid edge protection, chimney stacks, and temporary roofs tailored to weather-sensitive replacement jobs.",
  to: "/RoofingContractorsPage",
  icon: Home
}, {
  title: "Solar installers",
  description: "Specialist PV access solutions that protect panels and operatives during residential and commercial fits.",
  to: "/SolarInstallersPage",
  icon: SunMedium
}, {
  title: "Councils & public sector",
  description: "Fully vetted crews, clear audit trails, and NASC-aligned practices for municipal housing and education assets.",
  to: "/CouncilsAndPublicSectorPage",
  icon: Landmark
}, {
  title: "Property & facilities managers",
  description: "Planned building maintenance access, emergency repairs, and scheduled scaffold handovers across estate portfolios.",
  to: "/PropertyFacilitiesManagementPage",
  icon: Building
}];
const accreditations = [{
  label: "NASC Full Member"
}, {
  label: "CHAS Accredited"
}];
export default function BuyersHero() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 12
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4
      }
    }
  };
  return <section id="buyershero" data-nav="dark" className="relative overflow-hidden bg-secondary text-secondary-foreground py-16 md:py-24 lg:py-28 border-b border-border/20">
      {/* Subtle structural grid styling */}
      <div className="absolute inset-0 pointer-events-none opacity-5 bg-[linear-gradient(to_right,hsl(var(--secondary-foreground)/0.15)_1px,transparent_1px),linear-gradient(to_bottom,hsl(var(--secondary-foreground)/0.15)_1px,transparent_1px)] bg-[size:4rem_4rem]" />

      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial="hidden" animate="visible" variants={containerVariants} className="max-w-4xl mx-auto text-center">
          {/* Tagline / Accreditations Badge */}
          <motion.div variants={itemVariants} className="inline-flex items-center gap-2 mb-6">
            <Badge variant="outline" className="bg-secondary/70 border-secondary-foreground/20 text-secondary-foreground px-3 py-1 text-xs tracking-wide uppercase font-medium flex items-center gap-1.5">
              <HardHat className="size-3.5 text-primary" />
              <span>Tailored Access for Professional Buyers</span>
            </Badge>
          </motion.div>

          {/* Main Headline */}
          <motion.h1 variants={itemVariants} className="font-default text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-secondary-foreground leading-tight">
            Scaffolding built around your site schedule.
          </motion.h1>

          {/* Supporting Subheading */}
          <motion.p variants={itemVariants} className="mt-6 text-base sm:text-lg md:text-xl text-secondary-foreground/80 leading-relaxed max-w-3xl mx-auto">
            Whether you oversee commercial developments, solar rollouts, roofing packages, or municipal properties across Berkshire, Surrey, London, and the South East, BMF delivers certified access on time and safely.
          </motion.p>

          {/* Primary Action Row */}
          <motion.div variants={itemVariants} className="mt-8 sm:mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button asChild size="lg" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 shadow-md font-semibold px-6 py-3 text-base">
              <Link to="/ContactRequestQuotePage">
                Request a quote
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>

            <Button asChild size="lg" variant="outline" className="w-full sm:w-auto bg-secondary/80 border-secondary-foreground/25 text-secondary-foreground hover:bg-secondary hover:text-secondary-foreground px-6 py-3 text-base shadow-xs">
              <a href="tel:01344929900" className="text-secondary-foreground hover:text-secondary-foreground">
                <PhoneCall className="size-4 mr-1 text-primary" />
                Call 01344 929900
              </a>
            </Button>
          </motion.div>

          {/* Accreditation Badges */}
          <motion.div variants={itemVariants} className="mt-10 pt-8 border-t border-border/15 flex flex-wrap items-center justify-center gap-y-3 gap-x-6 text-xs sm:text-sm text-secondary-foreground/75">
            <span className="flex items-center gap-1.5 font-medium text-secondary-foreground">
              <ShieldCheck className="size-4 text-primary" />
              Accredited standards:
            </span>
            {accreditations.map((item, i) => <div key={item.label} data-index={i} className="flex items-center gap-1.5">
                <CheckCircle2 className="size-3.5 text-primary" />
                <span>{item.label}</span>
              </div>)}
          </motion.div>
        </motion.div>

        {/* Sectors Grid */}
        <div className="mt-14 sm:mt-18 lg:mt-20">
          <div className="text-center mb-8">
            <h2 className="text-xs uppercase tracking-widest text-secondary-foreground/60 font-semibold">
              Sectors and Buyer Types We Support
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {buyerSectors.map((sector, i) => {
            const IconComponent = sector.icon;
            return <div key={sector.title} data-index={i} className="group relative bg-secondary-foreground/5 hover:bg-secondary-foreground/10 border border-secondary-foreground/10 rounded-xl p-6 transition-[background-color,border-color,box-shadow] duration-200 hover:border-primary/40 flex flex-col justify-between">
                  <div>
                    <div className="size-11 rounded-lg bg-primary/15 text-primary flex items-center justify-center mb-4 transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                      <IconComponent className="size-5" />
                    </div>
                    <h3 className="text-lg font-semibold text-secondary-foreground mb-2 group-hover:text-primary transition-colors">
                      {sector.title}
                    </h3>
                    <p className="text-sm text-secondary-foreground/75 leading-relaxed">
                      {sector.description}
                    </p>
                  </div>

                  <div className="mt-6 pt-4 border-t border-secondary-foreground/10">
                    <Link to={sector.to} className="inline-flex items-center text-xs font-semibold text-primary group-hover:translate-x-0.5 transition-transform">
                      <span>Explore {sector.title.toLowerCase()} access</span>
                      <ArrowRight className="size-3.5 ml-1" />
                    </Link>
                  </div>
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}